import { ProcessNames } from '@parallel-fluidics/shared/constants';
import parseTime from 'components/HMI/components/parseTime';
import StartRunModalShell from 'features/plc/components/startRunModalShell';
import {
  PRESS_HMI_BONDING_CONFIG_FIELDS,
  PRESS_HMI_FORMING_CONFIG_FIELDS,
} from 'features/plc/contracts/pressHMI.fields';
import { PLC_PATHS } from 'features/plc/contracts/pressHMI.schema';
import {
  getCompatiblePressHMIBondingConfigValues,
  getCompatiblePressHMIFormingConfigValues,
} from 'features/plc/contracts/pressHMIConfig';
import {
  usePLC,
  usePLCValue,
  usePLCValues,
  usePLCWriteState,
} from 'features/plc/providers/PLCProvider';
import fetchLatestProcessLogForPress from 'features/processLog/api/fetchLatestProcessLogForPress';
import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import ConfigSection from './configSection';

const FORMING_TEMP_ALLOWED_MAP = Object.freeze({
  pmma: {
    upperFormingSetpoint: { min: 140, max: 160 },
    lowerFormingSetpoint: { min: 140, max: 160 },
  },
  cop: {
    upperFormingSetpoint: { min: 170, max: 185 },
    lowerFormingSetpoint: { min: 170, max: 185 },
  },
  pc: {
    upperFormingSetpoint: { min: 170, max: 185 },
    lowerFormingSetpoint: { min: 170, max: 185 },
  },
});

const BONDING_TEMP_ALLOWED_MAP = Object.freeze({
  x1: {
    lowerBondingSetpoint: { min: 105, max: 125 },
    upperBondingSetpoint: { min: 30, max: 70 },
    lowerSuperheatSetpoint: { min: 105, max: 135 },
    upperSuperheatSetpoint: { min: 30, max: 70 },
    lowerAnnealingSetpoint: { min: 95, max: 115 },
    upperAnnealingSetpoint: { min: 95, max: 130 },
    lowerFinalSetpoint: { min: 0, max: 70 },
    upperFinalSetpoint: { min: 0, max: 70 },
    lowerLoadingSetpoint: { min: 0, max: 70 },
    upperLoadingSetpoint: { min: 0, max: 70 },
  },
  pc: {
    lowerBondingSetpoint: { min: 140, max: 160 },
    upperBondingSetpoint: { min: 40, max: 80 },
    lowerSuperheatSetpoint: { min: 160, max: 180 },
    upperSuperheatSetpoint: { min: 40, max: 80 },
    lowerAnnealingSetpoint: { min: 110, max: 135 },
    upperAnnealingSetpoint: { min: 130, max: 160 },
    lowerFinalSetpoint: { min: 0, max: 70 },
    upperFinalSetpoint: { min: 0, max: 70 },
    lowerLoadingSetpoint: { min: 0, max: 70 },
    upperLoadingSetpoint: { min: 0, max: 70 },
  },
  pmma: {
    lowerBondingSetpoint: { min: 95, max: 110 },
    upperBondingSetpoint: { min: 30, max: 50 },
    lowerSuperheatSetpoint: { min: 115, max: 135 },
    upperSuperheatSetpoint: { min: 30, max: 50 },
    lowerAnnealingSetpoint: { min: 65, max: 90 },
    upperAnnealingSetpoint: { min: 75, max: 100 },
    lowerFinalSetpoint: { min: 0, max: 70 },
    upperFinalSetpoint: { min: 0, max: 70 },
    lowerLoadingSetpoint: { min: 0, max: 70 },
    upperLoadingSetpoint: { min: 0, max: 70 },
  },
  coc: {
    lowerBondingSetpoint: { min: 135, max: 155 },
    upperBondingSetpoint: { min: 30, max: 70 },
    lowerSuperheatSetpoint: { min: 145, max: 170 },
    upperSuperheatSetpoint: { min: 30, max: 70 },
    lowerAnnealingSetpoint: { min: 95, max: 115 },
    upperAnnealingSetpoint: { min: 105, max: 130 },
    lowerFinalSetpoint: { min: 0, max: 70 },
    upperFinalSetpoint: { min: 0, max: 70 },
    lowerLoadingSetpoint: { min: 0, max: 70 },
    upperLoadingSetpoint: { min: 0, max: 70 },
  },
  'non-standard': {
    lowerBondingSetpoint: { min: 90, max: 160 },
    upperBondingSetpoint: { min: 30, max: 70 },
    lowerSuperheatSetpoint: { min: 100, max: 180 },
    upperSuperheatSetpoint: { min: 30, max: 80 },
    lowerAnnealingSetpoint: { min: 65, max: 140 },
    upperAnnealingSetpoint: { min: 75, max: 160 },
    lowerFinalSetpoint: { min: 0, max: 70 },
    upperFinalSetpoint: { min: 0, max: 70 },
    lowerLoadingSetpoint: { min: 0, max: 70 },
    upperLoadingSetpoint: { min: 0, max: 70 },
  },
});

const PROCESS_CONFIG = {
  [ProcessNames.FORMING]: {
    configFields: PRESS_HMI_FORMING_CONFIG_FIELDS,
    getCompatibleConfigValues: getCompatiblePressHMIFormingConfigValues,
    getMaterialKey: (context) => context?.materialValue || null,
    getFieldErrorText: ({ field, currentValue, materialKey }) => {
      const range = FORMING_TEMP_ALLOWED_MAP[materialKey]?.[field.key];
      if (!range || typeof currentValue !== 'number') {
        return null;
      }

      if (currentValue > range.max) {
        return `Temperature should not exceed ${range.max}`;
      }

      if (currentValue < range.min) {
        return `Temperature should not be below ${range.min}`;
      }

      return null;
    },
    tempWarningMessage: 'Some temperatures fall outside the expected range for this material. Please investigate.',
  },
  [ProcessNames.BONDING]: {
    configFields: PRESS_HMI_BONDING_CONFIG_FIELDS,
    getCompatibleConfigValues: getCompatiblePressHMIBondingConfigValues,
    getMaterialKey: (context) => context?.capMaterialValue || null,
    getFieldErrorText: ({ field, currentValue, materialKey }) => {
      const range = BONDING_TEMP_ALLOWED_MAP[materialKey]?.[field.key];
      if (!range || typeof currentValue !== 'number') {
        return null;
      }

      if (currentValue > range.max) {
        return `Temperature should not exceed ${range.max}`;
      }

      if (currentValue < range.min) {
        return `Temperature should not be below ${range.min}`;
      }

      return null;
    },
    tempWarningMessage: 'Some temperatures fall outside the expected range for this cap material. Please investigate.',
  },
};

const formatFieldValue = ({ type, value }) => (
  type === 'TIME' ? parseTime(value) : String(value)
);

export default function StartRunModal({
  isOpen,
  close,
  process,
}) {
  const { pressId } = usePLC();
  const schemaVersion = usePLCValue(PLC_PATHS.meta.schemaVersion);
  const { runWrite } = usePLCWriteState();
  const [modalContext, setModalContext] = useState(null);
  const [previousConfigValues, setPreviousConfigValues] = useState(null);

  const processConfig = PROCESS_CONFIG[process];
  const { configFields } = processConfig;
  const plcValues = usePLCValues(configFields.map((field) => field.path));

  const currentConfigValues = useMemo(
    () => configFields.reduce((accumulator, field) => {
      const value = plcValues[field.path.name];

      if (value === undefined) {
        return accumulator;
      }

      return {
        ...accumulator,
        [field.key]: value,
      };
    }, {}),
    [configFields, plcValues],
  );

  const plcConfig = useMemo(() => ({
    schemaVersion,
    ...currentConfigValues,
  }), [currentConfigValues, schemaVersion]);

  const {
    errorTextByKey,
    warningTextByKey,
    warningItems,
  } = useMemo(() => {
    const errorByKey = {};
    const warningByKey = {};
    const materialKey = processConfig.getMaterialKey(modalContext);
    let hasTempWarning = false;
    let hasMismatchWarning = false;

    configFields.forEach((field) => {
      const currentValue = currentConfigValues[field.key];
      const previousValue = previousConfigValues?.[field.key];

      const errorText = processConfig.getFieldErrorText({
        field,
        currentValue,
        materialKey,
      });
      errorByKey[field.key] = errorText;
      if (errorText) {
        hasTempWarning = true;
      }

      if (previousValue !== undefined && currentValue !== previousValue) {
        hasMismatchWarning = true;
        warningByKey[field.key] = `Previous value was: ${formatFieldValue({
          type: field.type,
          value: previousValue,
        })}`;
        return;
      }

      warningByKey[field.key] = null;
    });

    const alertItems = [];
    if (hasMismatchWarning) {
      alertItems.push({
        type: 'warning',
        content: 'Some values have changed since the previous production run. Please double-check the configuration below before starting this run.',
        dismissible: false,
        id: 'mismatch_warning',
      });
    }

    if (hasTempWarning) {
      alertItems.push({
        type: 'error',
        content: processConfig.tempWarningMessage,
        dismissible: false,
        id: 'temp_warning',
      });
    }

    return {
      errorTextByKey: errorByKey,
      warningTextByKey: warningByKey,
      warningItems: alertItems,
    };
  }, [configFields, currentConfigValues, modalContext, previousConfigValues, processConfig]);

  const handleContextChange = useCallback(({
    production,
    projectId,
    pPartId,
    stepPPartId,
    materialValue,
    capMaterialValue,
  }) => {
    setModalContext({
      materialValue,
      capMaterialValue,
      pPartId,
      production,
      projectId,
      stepPPartId,
    });
  }, []);

  useEffect(() => {
    if (!modalContext?.production || !modalContext?.projectId) {
      setPreviousConfigValues(null);
      return;
    }

    const fetchProcessLog = async () => {
      const processLog = await fetchLatestProcessLogForPress({
        press: pressId,
        project: modalContext.projectId,
        pPart: modalContext.pPartId ?? null,
        process,
        production: true,
      });

      if (!processLog) {
        setPreviousConfigValues(null);
        return;
      }

      setPreviousConfigValues(processConfig.getCompatibleConfigValues({
        plcConfig: processLog.plcConfig,
        currentSchemaVersion: schemaVersion,
      }));
    };

    fetchProcessLog().catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Error fetching previous process log:', error);
      setPreviousConfigValues(null);
    });
  }, [
    modalContext?.pPartId,
    modalContext?.production,
    modalContext?.projectId,
    pressId,
    process,
    processConfig,
    schemaVersion,
  ]);

  return (
    <StartRunModalShell
      isOpen={isOpen}
      close={close}
      process={process}
      press={pressId}
      plcConfig={plcConfig}
      onStarted={async ({ processLogId, startMs }) => {
        // Anchor first, then the id, then enable, each only after the previous
        // write succeeded: automation must never start on a stale anchor or id.
        // A failed write is already surfaced through the writeError flashbar.
        if (!await runWrite({ path: PLC_PATHS.meta.runStartMs, value: startMs })) return;
        if (!await runWrite({ path: PLC_PATHS.meta.processLogId, value: processLogId })) return;
        await runWrite({ path: PLC_PATHS.cmd.automationEnable, value: true });
      }}
      onContextChange={handleContextChange}
      configSection={(
        <ConfigSection
          configFields={configFields}
          configValues={currentConfigValues}
          errorTextByKey={errorTextByKey}
          warningTextByKey={warningTextByKey}
        />
      )}
      isStartDisabled={!Number.isFinite(schemaVersion)}
      startDisabledMessage={Number.isFinite(schemaVersion)
        ? null
        : 'PLC schema version is unavailable. Start is disabled until the PressHMI metadata loads.'}
      warningItems={warningItems}
    />
  );
}

StartRunModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  process: PropTypes.string.isRequired,
};
